import type { Candle, CandidateRecord, ChartInterval } from '../types/market';
import { deepCandidateIntervals, deeplyValidateCandidate } from '../lib/deepCandidateEngine';
import { getVerifiedCandleHistory } from './candleHistory';
import { evaluateSupportingMethods } from '../lib/supporting/methodRegistry';

interface CandleHistoryResult {
  candles: Candle[];
  source: string;
  marketKind: 'spot' | 'futures';
  exchange: 'binance' | 'bybit' | 'okx';
}

interface CandleCacheEntry extends CandleHistoryResult {
  storedAt: number;
}

const candleCache = new Map<string, CandleCacheEntry>();
const inFlight = new Map<string, Promise<CandleHistoryResult>>();

function cacheTtl(interval: ChartInterval): number {
  switch (interval) {
    case '1m': return 20_000;
    case '5m': return 30_000;
    case '15m':
    case '30m': return 45_000;
    case '1h': return 90_000;
    case '2h':
    case '4h': return 3 * 60_000;
    case '1d': return 10 * 60_000;
    case '1w': return 30 * 60_000;
  }
}

function historyLimit(interval: ChartInterval): number {
  if (interval === '1w') return 120;
  if (interval === '1d') return 220;
  if (interval === '4h' || interval === '2h') return 260;
  if (interval === '1h') return 72;
  if (interval === '30m') return 96;
  if (interval === '15m') return 140;
  if (interval === '5m') return 320;
  return 200;
}

async function loadCandles(
  candidate: CandidateRecord,
  interval: ChartInterval,
  serverNow: number,
): Promise<CandleHistoryResult> {
  const preferred = candidate.marketKind;
  const limit = historyLimit(interval);
  const key = `${candidate.symbol}:${preferred}:${candidate.primaryExchange ?? 'auto'}:${interval}:${limit}`;
  const cached = candleCache.get(key);
  if (cached && Date.now() - cached.storedAt < cacheTtl(interval)) {
    return { candles: cached.candles, source: cached.source, marketKind: cached.marketKind, exchange: cached.exchange };
  }

  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = (async (): Promise<CandleHistoryResult> => {
    const result = await getVerifiedCandleHistory(candidate.symbol, interval, limit, preferred, serverNow, candidate.primaryExchange);
    candleCache.set(key, { ...result, storedAt: Date.now() });
    return result;
  })();

  inFlight.set(key, request);
  try {
    return await request;
  } finally {
    inFlight.delete(key);
  }
}

async function mapWithConcurrency<T, R>(items: T[], concurrency: number, task: (item: T) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;
  async function runner(): Promise<void> {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await task(items[index]!);
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => runner()));
  return results;
}

export async function evaluateSupportingCandidate(
  candidate: CandidateRecord,
  now: number,
): Promise<import('../lib/supporting/types').SupportingAnalysis> {
  const result = await loadCandles(candidate, candidate.horizon, now);
  const completedCandles = result.candles.filter((candle) => candle.closed);
  return evaluateSupportingMethods(
    { candles: completedCandles, direction: candidate.direction, now },
    candidate.horizon,
  );
}

export async function deepAnalyzeCandidate(
  candidate: CandidateRecord,
  now: number,
): Promise<CandidateRecord> {
  const intervals = deepCandidateIntervals(candidate.horizon);
  const results = await mapWithConcurrency(intervals, 2, async (interval) => {
    try {
      return { status: 'fulfilled' as const, value: { interval, ...(await loadCandles(candidate, interval, now)) } };
    } catch (reason) {
      return { status: 'rejected' as const, reason };
    }
  });

  const candleMap: Partial<Record<ChartInterval, Candle[]>> = {};
  const issues: string[] = [];
  const candleSources: string[] = [];
  const horizonSources: Partial<Record<ChartInterval, 'spot' | 'futures'>> = {};
  for (let index = 0; index < results.length; index += 1) {
    const interval = intervals[index]!;
    const result = results[index]!;
    if (result.status === 'fulfilled') {
      candleMap[interval] = result.value.candles;
      candleSources.push(result.value.source);
      horizonSources[interval] = result.value.marketKind;
    } else {
      issues.push(`${interval}: ${result.reason instanceof Error ? result.reason.message : 'history unavailable'}`);
    }
  }

  const candidateWithSources: CandidateRecord = {
    ...candidate,
    sourceMarkets: [...new Set([...candidate.sourceMarkets, ...candleSources])],
  };
  const horizonCandles = candleMap[candidate.horizon];
  if (!horizonCandles || horizonCandles.length < 24) {
    const validated = deeplyValidateCandidate({
      candidate: candidateWithSources,
      candleMap,
      now,
      validation: {
        status: 'failed',
        coverage: intervals.length > 0 ? Object.keys(candleMap).length / intervals.length : 0,
        issues,
      },
    });
    return { ...validated, coreScore: validated.candidateScore, priorityScore: validated.candidateScore + (validated.supportingScore ?? 0) };
  }

  const activeIndex = horizonCandles.length - 1;
  const active = activeIndex >= 0 ? horizonCandles[activeIndex] : undefined;
  if (active && !active.closed && candidate.currentPrice > 0 && horizonSources[candidate.horizon] === candidate.marketKind) {
    horizonCandles[activeIndex] = { ...active, close: candidate.currentPrice, high: Math.max(active.high, candidate.currentPrice), low: Math.min(active.low, candidate.currentPrice) };
  }

  const coverage = Object.keys(candleMap).length / intervals.length;
  const validated = deeplyValidateCandidate({
    candidate: candidateWithSources,
    candleMap,
    now,
    validation: {
      status: coverage === 1 ? 'complete' : 'partial',
      coverage,
      issues,
    },
  });

  // Supporting methods are evaluated only from completed candles. The live active
  // candle is useful to the Core timing engine, but it must not leak unfinished
  // OHLCV into independent historical methods.
  const supportingCandles = horizonCandles.filter((candle) => candle.closed);
  const supportingAnalysis = evaluateSupportingMethods(
    { candles: supportingCandles, direction: validated.direction, now },
    candidate.horizon,
  );
  return {
    ...validated,
    coreScore: validated.candidateScore,
    supportingScore: supportingAnalysis.score,
    priorityScore: validated.candidateScore + supportingAnalysis.score,
    supportingAnalysis,
  };
}

export function clearDeepCandidateCache(): void {
  candleCache.clear();
  inFlight.clear();
}
